import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Grounded course coach — answers only from this course and cites the chapter. */
export const CoachPanel = ({ courseId, courseTitle, chapterLabel, theme }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`coach-${courseId}-${Math.random().toString(36).slice(2, 9)}`);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const ask = async (question) => {
    const q = (question || input).trim();
    if (!q || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q }, { role: 'assistant', text: '' }]);
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/learning-hub/coach/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course_id: courseId,
          session_id: sessionRef.current,
          user_id: localStorage.getItem('cness_username') || 'guest',
          message: q,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const frame of frames) {
          const line = frame.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.t) {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { role: 'assistant', text: next[next.length - 1].text + payload.t };
                return next;
              });
            }
          } catch { /* partial frame */ }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: 'assistant', text: 'The coach is unavailable right now. Please try again.' };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="coach-open-btn"
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-5 py-3.5 rounded-full text-white font-semibold shadow-2xl hover:scale-[1.03] transition-transform"
        style={{ backgroundColor: theme.primary }}
      >
        <MessageCircle className="h-5 w-5" /> Ask the coach
      </button>
    );
  }

  return (
    <div
      data-testid="coach-panel"
      className="fixed bottom-6 right-6 z-40 w-[min(26rem,calc(100vw-3rem))] max-h-[32rem] flex flex-col bg-white rounded-3xl border border-slate-200 shadow-2xl overflow-hidden"
    >
      <div className="flex items-center justify-between px-5 py-4 text-white" style={{ backgroundColor: theme.primary }}>
        <div>
          <p className="text-sm font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4" /> Learning Coach</p>
          <p className="text-xs text-white/70 mt-0.5">Answers only from {courseTitle}</p>
        </div>
        <button onClick={() => setOpen(false)} data-testid="coach-close-btn" aria-label="Close coach">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Ask anything about this course. The coach cites the chapter it used and never scores you.
            </p>
            {[`Explain ${chapterLabel} in simple words`, 'What should I do differently tomorrow?', 'Why does this matter commercially?'].map((s, i) => (
              <button
                key={s}
                onClick={() => ask(s)}
                data-testid={`coach-suggestion-${i}`}
                className="w-full text-left text-sm px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`coach-message-${m.role}-${i}`}
            className={`text-sm leading-relaxed rounded-2xl px-4 py-3 ${
              m.role === 'user' ? 'bg-slate-900 text-white ml-8' : 'bg-slate-50 text-slate-800 mr-4'
            }`}
          >
            {m.text || <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); ask(); }}
        className="flex items-center gap-2 border-t border-slate-200 p-3"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          data-testid="coach-input"
          placeholder="Ask about this course…"
          className="flex-1 text-sm px-4 py-2.5 rounded-full bg-slate-50 focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': theme.accent }}
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="coach-send-btn"
          className="shrink-0 h-10 w-10 rounded-full text-white flex items-center justify-center disabled:bg-slate-300"
          style={busy || !input.trim() ? {} : { backgroundColor: theme.primary }}
          aria-label="Send question"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
};

export default CoachPanel;
